import { useCallback, useRef, useEffect } from 'react';
import { PageSearchResult } from './spotlight.types';
import { useSpotlightStore } from './spotlight.store';

const MAX_RESULTS = 50;
const HIGHLIGHT_DURATION = 3000;

export const usePageSearch = () => {
  const { setPageSearchResults } = useSpotlightStore();
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeHighlightRef = useRef<HTMLElement | null>(null);
  const justSelectedRef = useRef<boolean>(false);

  // Search for text on the current page
  const searchPageText = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) {
      setPageSearchResults([]);
      return;
    }

    const results: PageSearchResult[] = [];
    const lowerQuery = searchQuery.toLowerCase();

    // Only search within .neo-app-content
    const appContent = document.querySelector('.neo-app-content');
    if (!appContent) {
      setPageSearchResults([]);
      return;
    }

    // Get all text nodes in the main content area (exclude spotlight itself)
    const walker = document.createTreeWalker(appContent, NodeFilter.SHOW_TEXT, {
      acceptNode: node => {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest('.neo-spotlight-backdrop')) return NodeFilter.FILTER_REJECT;
        if (parent.closest('script, style, noscript')) return NodeFilter.FILTER_REJECT;
        if (parent.offsetParent === null && parent.tagName !== 'BODY') return NodeFilter.FILTER_REJECT;
        const text = node.textContent?.trim();
        if (!text || text.length < 2) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let index = 0;
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node.textContent || '';
      const lowerText = text.toLowerCase();
      if (lowerText.includes(lowerQuery)) {
        const matchIndex = lowerText.indexOf(lowerQuery);
        const contextStart = Math.max(0, matchIndex - 30);
        const contextEnd = Math.min(text.length, matchIndex + searchQuery.length + 30);
        let context = text.slice(contextStart, contextEnd).trim();
        if (contextStart > 0) context = '...' + context;
        if (contextEnd < text.length) context = context + '...';

        results.push({
          text: text.slice(matchIndex, matchIndex + searchQuery.length),
          context,
          textNode: node,
          matchIndex,
          index: index++,
        });
      }
    }

    setPageSearchResults(results.slice(0, MAX_RESULTS));
  }, [setPageSearchResults]);

  // Clear any existing highlights
  const clearHighlights = useCallback(() => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }

    const restoredNodes: { parent: Node; textNode: Text; text: string }[] = [];
    document.querySelectorAll('mark.neo-spotlight-page-highlight').forEach(el => {
      const parent = el.parentNode;
      if (parent) {
        const textContent = el.textContent || '';
        const newTextNode = document.createTextNode(textContent);
        parent.replaceChild(newTextNode, el);
        parent.normalize();
        restoredNodes.push({ parent, textNode: newTextNode, text: textContent });
      }
    });

    return restoredNodes;
  }, []);

  // Highlight and scroll to a page search result
  const highlightPageResult = useCallback((result: PageSearchResult, onComplete?: () => void) => {
    const restoredNodes = clearHighlights();

    // Find the text node to highlight
    let targetNode: Node | null = result.textNode;
    const targetText = result.context
      .replace(/^\.\.\./, '')
      .replace(/\.\.\.$/, '')
      .trim();

    // If the original text node is no longer in the document, find it among restored nodes
    if (!document.contains(targetNode)) {
      const restored = restoredNodes.find(r => r.text === result.textNode.textContent);
      if (restored) {
        const walker = document.createTreeWalker(restored.parent, NodeFilter.SHOW_TEXT);
        let node: Node | null;
        while ((node = walker.nextNode())) {
          if (node.textContent?.includes(targetText)) {
            targetNode = node;
            break;
          }
        }
      }
    }

    const text = targetNode?.textContent || '';
    const parent = targetNode?.parentNode;

    if (parent && targetNode && targetNode.nodeType === Node.TEXT_NODE) {
      const mark = document.createElement('mark');
      mark.className = 'neo-spotlight-page-highlight';
      mark.textContent = text;

      parent.replaceChild(mark, targetNode);

      // Scroll to the mark element
      let element: HTMLElement | null = mark;
      const scrollableParents: HTMLElement[] = [];
      while (element) {
        const parentEl = element.parentElement;
        if (parentEl) {
          const style = getComputedStyle(parentEl);
          const overflowY = style.overflowY;
          const overflowX = style.overflowX;
          const isScrollableY = (overflowY === 'auto' || overflowY === 'scroll') && parentEl.scrollHeight > parentEl.clientHeight;
          const isScrollableX = (overflowX === 'auto' || overflowX === 'scroll') && parentEl.scrollWidth > parentEl.clientWidth;
          if (isScrollableY || isScrollableX) {
            scrollableParents.push(parentEl);
          }
        }
        element = parentEl;
      }

      // Scroll each scrollable parent to bring the mark into view
      scrollableParents.forEach(scrollParent => {
        const markRect = mark.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();

        if (markRect.top < parentRect.top || markRect.bottom > parentRect.bottom) {
          const scrollTop = mark.offsetTop - scrollParent.clientHeight / 2;
          scrollParent.scrollTop = scrollTop;
        }
        if (markRect.left < parentRect.left || markRect.right > parentRect.right) {
          const scrollLeft = mark.offsetLeft - scrollParent.clientWidth / 2;
          scrollParent.scrollLeft = scrollLeft;
        }
      });

      // After scrolling containers, scroll the main window with smooth behavior
      setTimeout(() => {
        mark.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }, 100);

      activeHighlightRef.current = mark;

      // Fade out highlight after duration
      highlightTimeoutRef.current = setTimeout(() => {
        mark.classList.add('neo-spotlight-highlight-fade');
        highlightTimeoutRef.current = null;
        activeHighlightRef.current = null;
      }, HIGHLIGHT_DURATION);
    }

    onComplete?.();
  }, [clearHighlights]);

  // Check if element is clickable
  const isClickable = useCallback((el: HTMLElement): boolean => {
    if (el.tagName === 'A' || el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
      return true;
    }
    const role = el.getAttribute('role');
    if (role === 'button' || role === 'link' || role === 'menuitem' || role === 'tab') {
      return true;
    }
    if (el.onclick !== null || el.hasAttribute('onclick')) {
      return true;
    }
    const attrs = el.getAttributeNames();
    for (const attr of attrs) {
      const lowerAttr = attr.toLowerCase();
      if (lowerAttr === 'routerlink' || lowerAttr === 'ng-click' || lowerAttr === '(click)' || lowerAttr.startsWith('_ngcontent')) {
        if (el.tagName === 'A' || el.tagName === 'BUTTON' || getComputedStyle(el).cursor === 'pointer') {
          return true;
        }
      }
    }
    if (el.hasAttribute('tabindex') && el.getAttribute('tabindex') !== '-1') {
      return true;
    }
    if (getComputedStyle(el).cursor === 'pointer') {
      return true;
    }
    return false;
  }, []);

  // Find nearest clickable element
  const findClickableElement = useCallback((startElement: HTMLElement): HTMLElement | null => {
    const tableRow = startElement.closest('tr');
    if (tableRow) {
      const allElements = tableRow.querySelectorAll('*');
      for (const el of allElements) {
        if (el instanceof HTMLElement && isClickable(el)) {
          return el;
        }
      }
    }

    let element: HTMLElement | null = startElement;
    while (element) {
      if (isClickable(element)) {
        return element;
      }
      element = element.parentElement;
    }

    let searchRoot: HTMLElement | null = startElement.parentElement;
    for (let i = 0; i < 5 && searchRoot; i++) {
      const allElements = searchRoot.querySelectorAll('*');
      for (const el of allElements) {
        if (el instanceof HTMLElement && isClickable(el)) {
          return el;
        }
      }
      searchRoot = searchRoot.parentElement;
    }

    return null;
  }, [isClickable]);

  // Listen for Enter key to click nearest clickable element when highlight is active
  useEffect(() => {
    const handleEnterKey = (e: KeyboardEvent) => {
      if (justSelectedRef.current) {
        justSelectedRef.current = false;
        return;
      }
      if (e.key === 'Enter' && activeHighlightRef.current) {
        e.preventDefault();

        const clickableElement = findClickableElement(activeHighlightRef.current);

        if (clickableElement) {
          clickableElement.click();
          activeHighlightRef.current?.classList.add('neo-spotlight-highlight-fade');
          activeHighlightRef.current = null;
          if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = null;
          }
        }
      }
    };

    document.addEventListener('keydown', handleEnterKey);
    return () => document.removeEventListener('keydown', handleEnterKey);
  }, [findClickableElement]);

  // Mark that we just selected (to prevent the Enter key handler from triggering)
  const markJustSelected = useCallback(() => {
    justSelectedRef.current = true;
  }, []);

  return {
    searchPageText,
    highlightPageResult,
    clearHighlights,
    markJustSelected,
  };
};
